import {
  entityRefToUrn,
  generateURNByKind,
  RefsAndCommitUids,
} from '@agilelab/plugin-wb-builder-common';
import {
  EntityIcon,
  customAlertApiRef,
  WbAutocomplete,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import { parseEntityRef, stringifyEntityRef } from '@mainstage/catalog-model';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@mainstage/integration-react';
import {
  catalogApiRef,
  entityRouteRef,
  getEntitySourceLocation,
} from '@mainstage/plugin-catalog-react';
import { Box, InputLabel, Popper, makeStyles } from '@material-ui/core';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import clsx from 'clsx';
import parseGitUrl from 'git-url-parse';
import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { panelCatalogApiRef } from '../../../../api';
import { checkExistingEditTemplate } from '../../../common/utils';
import { useEditorPageContext } from '../../context/useEditorPageContext';
import { EntityWithIcon } from '../../types';
import {
  CARD_AVATAR_DIMENSION,
  CARD_HEIGHT,
  CARD_REDUCED_HEIGHT,
} from './const';
import { AlphaEntity, EntityStatus } from '@mainstage/catalog-model/alpha';
import { EntityCardMenu } from './EntityCardMenu';
import { EntityCardTitle } from './EntityCardTitle';
import { getGitSourceUrl, resolveGitSourceLocation } from '../../utils';

type StylesProps = { isActiveEntity?: boolean } | undefined;

const useStyles = makeStyles(theme => ({
  container: (props: StylesProps = { isActiveEntity: true }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    padding: '4px 8px',
    gap: '8px',
    minHeight: props?.isActiveEntity ? CARD_HEIGHT : CARD_REDUCED_HEIGHT,
    maxHeight: props?.isActiveEntity ? CARD_HEIGHT : CARD_REDUCED_HEIGHT,
    height: props?.isActiveEntity ? CARD_HEIGHT : CARD_REDUCED_HEIGHT,
    borderRadius: '4px',
    minWidth: 'fit-content',
  }),
  avatar: {
    width: CARD_AVATAR_DIMENSION,
    height: CARD_AVATAR_DIMENSION,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
  },
}));

interface Props {
  entity: EntityWithIcon;
  style?: CSSProperties;
  className?: string;
  isActiveEntity?: boolean;
  short?: boolean;
}

export const EntityCard: FC<Props> = ({
  entity,
  style,
  className,
  isActiveEntity,
  short = false,
}) => {
  const classes = useStyles({ isActiveEntity: isActiveEntity });

  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    changeBranches,
    currRelease,
    editorRef,
    focusedEntityUrn,
    setFocusedEntityUrn,
    parent,
    descriptorStateWithComponents,
    fetchReleaseState,
    currentDescriptorViewMode,

    parsedDescriptor,
    customViewPageIndex,
    setCustomViewPageIndex,
  } = useEditorPageContext();

  const isCustomViewActive = useMemo(
    () => currentDescriptorViewMode === 'customView',
    [currentDescriptorViewMode],
  );

  const name: string = (entity?.spec?.mesh as any)?.name || '';
  const entityStatus: EntityStatus | undefined =
    (entity as AlphaEntity)?.status || undefined;
  const error: string | undefined =
    entityStatus?.items?.[0]?.error?.message || undefined;

  const [errorMessage, setErrorMessage] = useState('');
  const entitySourceLocation = useMemo(() => {
    return entity ? getEntitySourceLocation(entity, scmIntegrationsApi) : null;
  }, [entity, scmIntegrationsApi]);
  const [refs, setRefs] = useState<RefsAndCommitUids | null>(null);
  const gitSourceLocation = useMemo(
    () =>
      entitySourceLocation?.locationTargetUrl
        ? resolveGitSourceLocation(
            entitySourceLocation.locationTargetUrl,
            entitySourceLocation.integrationType,
            refs,
          )
        : undefined,
    [entitySourceLocation, refs],
  );
  const needsRefResolution =
    (entitySourceLocation?.integrationType === 'github' ||
      entitySourceLocation?.integrationType === 'gitlab') &&
    !!parseGitUrl(entitySourceLocation.locationTargetUrl).filepathtype &&
    !gitSourceLocation;

  const entityUrn = generateURNByKind(entity.metadata.name, entity.kind);
  const entityRef = entity
    ? stringifyEntityRef({
        name: entity?.metadata?.name || '',
        kind: entity?.kind,
        namespace: entity?.metadata?.namespace || '',
      })
    : undefined;
  const entityRoute = useRouteRef(entityRouteRef);

  const defaultBranch = useMemo(() => {
    if (
      fetchReleaseState.loading ||
      !fetchReleaseState.value ||
      currRelease === null ||
      needsRefResolution
    )
      return '';

    const createdFrom = currRelease?.metadata?.createdFrom?.find(
      f => f.entityRef === entityRef,
    );
    const branchFromCreatedFrom = createdFrom?.branch?.replace(/%2F/gi, '/');
    const branchFromLocationUrl =
      gitSourceLocation?.ref ??
      (entitySourceLocation?.locationTargetUrl
        ? parseGitUrl(entitySourceLocation.locationTargetUrl).ref || 'master'
        : 'master');

    const rightBranch = branchFromCreatedFrom ?? branchFromLocationUrl;
    return rightBranch.replace('refs/heads/', '').replace('refs/tags/', '');
  }, [
    fetchReleaseState.loading,
    fetchReleaseState.value,
    currRelease,
    entitySourceLocation?.locationTargetUrl,
    entityRef,
    gitSourceLocation?.ref,
    needsRefResolution,
  ]);

  const defaultRefType = useMemo<'branch' | 'tag'>(() => {
    if (
      fetchReleaseState.loading ||
      !fetchReleaseState.value ||
      currRelease === null
    )
      return 'branch';
    const createdFrom = currRelease?.metadata?.createdFrom?.find(
      f => f.entityRef === entityRef,
    );
    return createdFrom?.refType ?? gitSourceLocation?.refType ?? 'branch';
  }, [
    fetchReleaseState.loading,
    fetchReleaseState.value,
    currRelease,
    entityRef,
    gitSourceLocation?.refType,
  ]);

  const [targetBranch, setTargetBranch] = useState<string>(defaultBranch);
  const [targetRefType, setTargetRefType] = useState<'branch' | 'tag'>(
    defaultRefType,
  );

  /**
   * this effects sets the right default branch when the editor tab opens.
   * This is because `master` is not always the default branch, for example for
   * dp created using the `new version` feature. Another case is that, after a new draft release
   * is created from different branches, when refreshing the editor you want to see set as default
   * the branches from which that draft release was created.
   */
  useEffect(() => {
    if (defaultBranch !== '' && entityRef) {
      changeBranches(entityRef, defaultBranch, defaultRefType);
      setTargetBranch(defaultBranch);
      setTargetRefType(defaultRefType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultBranch, defaultRefType, entityRef]);

  const location = useLocation();

  const route = useMemo(() => {
    if (entity) {
      const data = {
        name: entity?.metadata.name ?? '',
        kind: entity?.kind,
        namespace: entity?.metadata.namespace ?? '',
      };
      const ref = parseEntityRef(data);
      const preUrl = location.pathname.includes('my-projects')
        ? '/my-projects'
        : '';

      return `${preUrl}${entityRoute(ref)}/editor`;
    }
    return '/';
  }, [entity, entityRoute, location.pathname]);

  const editorWizardRoute = useMemo(() => {
    if (!entity) return '#';
    const { namespace = 'default', name: entityName } = entity.metadata;
    const kindLowerCase = entity.kind.toLowerCase();

    const hrefEditorWizard = `/scaffolder/${encodeURIComponent(
      namespace,
    )}/${encodeURIComponent(kindLowerCase)}/${encodeURIComponent(
      entityName,
    )}/editor-wizard?targetBranch=${targetBranch}`;

    return hrefEditorWizard;
  }, [entity, targetBranch]);

  useEffect(() => {
    checkExistingEditTemplate({ catalogApi, entity, setErrorMessage });
  }, [entity, setErrorMessage, catalogApi]);

  const [refsLoading, setRefsLoading] = useState(false);
  const hasFetchedRefs = useRef(false);

  const fetchRefsOnce = useCallback(() => {
    if (hasFetchedRefs.current || !entitySourceLocation?.locationTargetUrl)
      return;
    hasFetchedRefs.current = true;
    setRefsLoading(true);
    panelCatalog
      .getRefs(entitySourceLocation.locationTargetUrl)
      .then(setRefs)
      .catch(refsError => {
        hasFetchedRefs.current = false;
        alertApi.post({ error: refsError, severity: 'error' });
      })
      .finally(() => setRefsLoading(false));
  }, [entitySourceLocation?.locationTargetUrl, panelCatalog, alertApi]);

  useEffect(() => {
    if (needsRefResolution) fetchRefsOnce();
  }, [needsRefResolution, fetchRefsOnce]);

  const refOptions = useMemo(() => {
    const branches = Object.keys(refs?.branches ?? {}).map(refName => ({
      refName,
      refType: 'branch' as const,
    }));
    const tags = Object.keys(refs?.tags ?? {}).map(refName => ({
      refName,
      refType: 'tag' as const,
    }));
    return [...branches, ...tags];
  }, [refs]);

  const refAutocompleteId = useId();

  const highlightHandleClick = useCallback(() => {
    if (editorRef?.current) {
      const entityIdLine = editorRef.current.findLineContainingId(entityUrn);
      const lineRanges =
        editorRef.current.selectLinesOfOtherSections(entityUrn);

      if (lineRanges.ranges()?.length !== 0) {
        setFocusedEntityUrn(entityUrn);
        editorRef.current.highlightLineRanges(lineRanges, 'blur-line');
        editorRef.current.goToLine(entityIdLine, false, 200);
      }
    }
  }, [editorRef, entityUrn, setFocusedEntityUrn]);

  useEffect(() => {
    // check if the entity is the active one, if is a component so has a parent and the descriptor is not loading.
    let dispose: (() => void) | undefined;
    if (isActiveEntity && parent && !descriptorStateWithComponents.loading) {
      // verifies if the state value has been propagated to the inner state of monaco editor
      const isContentReady = () => {
        return (
          descriptorStateWithComponents.value === editorRef?.current?.getValue()
        );
      };

      const apply = () => {
        if (isContentReady()) {
          highlightHandleClick();
          dispose?.();
        }
      };

      if (isContentReady()) {
        highlightHandleClick();
      }
      // if inner monaco value still not updated,
      // subscribe to onDidChangeModelContent waiting for monaco to update it (in the callback we highlight), also stores the dispose function for later cleanup
      else
        dispose = editorRef?.current?.onDidChangeModelContent(apply)?.dispose;
    }
    return () => {
      dispose?.();
    };
  }, [
    descriptorStateWithComponents.loading,
    descriptorStateWithComponents.value,
    isActiveEntity,
    editorRef,
    highlightHandleClick,
    parent,
  ]);

  // Auto-navigate to this component in custom view when the descriptor first loads
  const prevParsedDescriptor = useRef(parsedDescriptor);
  useEffect(() => {
    const wasNull = prevParsedDescriptor.current === null;
    prevParsedDescriptor.current = parsedDescriptor;
    if (
      wasNull &&
      parsedDescriptor !== null &&
      isActiveEntity &&
      parent &&
      isCustomViewActive
    ) {
      const comps: any[] = parsedDescriptor.components ?? [];
      const idx = comps.findIndex(c => c.id === entityUrn);
      if (idx !== -1) setCustomViewPageIndex(idx + 1);
    }
  }, [
    parsedDescriptor,
    isActiveEntity,
    parent,
    isCustomViewActive,
    entityUrn,
    setCustomViewPageIndex,
  ]);

  const locationTargetUrl = useMemo(() => {
    const str = entitySourceLocation?.locationTargetUrl;
    if (!str) return str;
    if (gitSourceLocation) {
      return getGitSourceUrl(str, gitSourceLocation, targetBranch);
    }
    if (needsRefResolution) return undefined;
    // For Azure DevOps URLs the version param carries a GB/GT prefix; replace both prefix and name.
    const prefix = targetRefType === 'tag' ? 'GT' : 'GB';
    const adoReplaced = str.replace(
      /([?&]version=)(?:GB|GT)([^&]*)/,
      `$1${prefix}${encodeURIComponent(targetBranch)}`,
    );
    if (adoReplaced !== str) return adoReplaced;
    // Fallback for non-ADO providers: replace the branch name in the path.
    const lastIndex = str.lastIndexOf(defaultBranch);
    if (lastIndex === -1) return str;
    return `${str.substring(0, lastIndex)}${targetBranch}${str.substring(
      lastIndex + defaultBranch.length,
    )}`;
  }, [
    entitySourceLocation?.locationTargetUrl,
    targetBranch,
    targetRefType,
    defaultBranch,
    gitSourceLocation,
    needsRefResolution,
  ]);

  const componentIndexInDescriptor = useMemo(
    () =>
      (parsedDescriptor?.components ?? []).findIndex(
        (c: any) => c.id === entityUrn,
      ),
    [parsedDescriptor, entityUrn],
  );

  const onFocusToggle = useCallback(() => {
    if (isCustomViewActive) {
      const targetPage =
        componentIndexInDescriptor !== -1 ? componentIndexInDescriptor + 1 : 0;
      // toggle: if already on this page, go back to system (0)
      setCustomViewPageIndex(prev => (prev === targetPage ? 0 : targetPage));
      return;
    }

    if (focusedEntityUrn === entityUrn) {
      setFocusedEntityUrn(null);
      editorRef?.current?.highlightLines([]);
      return;
    }
    highlightHandleClick();
  }, [
    isCustomViewActive,
    componentIndexInDescriptor,
    setCustomViewPageIndex,
    editorRef,
    focusedEntityUrn,
    entityUrn,
    highlightHandleClick,
    setFocusedEntityUrn,
  ]);

  let focused: boolean;
  if (isCustomViewActive) {
    if (componentIndexInDescriptor !== -1) {
      focused = customViewPageIndex === componentIndexInDescriptor + 1;
    } else {
      focused = customViewPageIndex === 0;
    }
  } else {
    focused = entityUrn === focusedEntityUrn;
  }

  const taxonomyRef = (entity?.metadata?.classDetails as any).taxonomy;

  const taxonomyUrn = useMemo(() => {
    try {
      return entityRefToUrn(taxonomyRef);
    } catch (_e) {
      return undefined;
    }
  }, [taxonomyRef]);

  const label = 'Source';

  return (
    <Box style={style} className={clsx(classes.container, className)}>
      <EntityIcon
        title={name}
        className={classes.avatar}
        useCaseTemplateId={(entity?.spec?.mesh as any)?.useCaseTemplateId}
        kind={entity.kind.toLowerCase()}
        taxonomyUrn={taxonomyUrn}
        style={{ flexShrink: 0 }}
        disableSystemForceDefault
      />
      <Box style={{ flex: 1 }} className={classes.column}>
        <EntityCardTitle
          name={name}
          loading={descriptorStateWithComponents.loading}
          route={route}
          error={error}
        />

        {isActiveEntity && targetBranch !== '' && (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            {!short && (
              <InputLabel
                htmlFor={refAutocompleteId}
                style={{ whiteSpace: 'nowrap', marginBottom: 0 }}
              >
                {label}:
              </InputLabel>
            )}
            <WbAutocomplete<
              { refName: string; refType: 'branch' | 'tag' },
              false,
              true,
              false
            >
              id={refAutocompleteId}
              options={refOptions}
              inputProps={short ? { 'aria-label': label } : undefined}
              value={{ refName: targetBranch, refType: targetRefType }}
              onChange={(_, newVal) => {
                if (
                  newVal &&
                  typeof newVal !== 'string' &&
                  (newVal.refName !== targetBranch ||
                    newVal.refType !== targetRefType)
                ) {
                  setTargetBranch(newVal.refName);
                  setTargetRefType(newVal.refType);
                  changeBranches(entityRef!, newVal.refName, newVal.refType);
                  setFocusedEntityUrn(null);
                }
              }}
              getOptionLabel={opt => opt.refName}
              getOptionSelected={(opt, val) =>
                opt.refName === val.refName && opt.refType === val.refType
              }
              groupBy={opt => (opt.refType === 'branch' ? 'Branches' : 'Tags')}
              onOpen={fetchRefsOnce}
              loading={refsLoading}
              loadingText="Loading…"
              noOptionsText="No results"
              disabled={descriptorStateWithComponents.loading}
              disableClearable
              small
              style={{
                minWidth: 0,
                width: 150,
                // make input not take the background color (for example selected entity has bkg color)
                background: 'white',
                borderRadius: 4,
              }}
              PopperComponent={props => (
                <Popper {...props} style={{ ...props.style, minWidth: 280 }} />
              )}
              startAdornment={
                targetRefType === 'tag' ? (
                  <LocalOfferIcon
                    aria-hidden
                    style={{ fontSize: 13, flexShrink: 0 }}
                  />
                ) : (
                  <CallSplitIcon
                    aria-hidden
                    style={{ fontSize: 13, flexShrink: 0 }}
                  />
                )
              }
              renderOption={opt => (
                <WbTruncatedTypographyWrapper
                  value={opt.refName}
                  aria-label={`${opt.refType === 'tag' ? 'Tag' : 'Branch'}: ${
                    opt.refName
                  }`}
                />
              )}
            />
          </Box>
        )}
      </Box>
      <Box style={{ display: 'flex', gap: '2px' }}>
        <EntityCardMenu
          short={short}
          focused={focused}
          onFocusToggle={onFocusToggle}
          editorWizardRoute={editorWizardRoute}
          locationTargetUrl={locationTargetUrl}
          errorMessage={errorMessage}
          isLoading={descriptorStateWithComponents.loading}
          disableEdit={targetRefType === 'tag'}
        />
      </Box>
    </Box>
  );
};
